import { PASSCODE_IDB_STORE } from './browser/idb';
import { getStorageAdapter, STORAGE_KEYS, getChunkedItem, setChunkedItem, removeChunkedItem } from './storage';

const IV_LENGTH = 12;
const SALT = 'harder better faster stronger';

let currentPasscodeHash: ArrayBuffer | undefined;

export class UnrecoverablePasscodeError extends Error {}

export function getPasscodeHash() {
  return currentPasscodeHash;
}

export function setPasscodeHash(passcodeHash: ArrayBuffer) {
  currentPasscodeHash = passcodeHash;
}

export async function setupPasscode(passcode: string) {
  currentPasscodeHash = await sha256(passcode);
}

export async function encryptSession(sessionJson?: string, globalJson?: string, sharedStateJson?: string) {
  if (!currentPasscodeHash) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing current passcode');
    throw new Error('[api/passcode] Missing current passcode');
  }

  await Promise.all([
    storeEncrypted('sessionEncrypted', sessionJson),
    storeEncrypted('globalEncrypted', globalJson),
    storeEncrypted('sharedStateEncrypted', sharedStateJson),
  ]);
}

async function storeEncrypted(key: string, json?: string) {
  if (!json) return;

  const encrypted = await aesEncrypt(json, currentPasscodeHash!);
  await store(key, encrypted);
}

export async function decryptSessionByCurrentHash() {
  if (!currentPasscodeHash) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing current passcode');
    throw new Error('[api/passcode] Missing current passcode');
  }

  const [sessionEncrypted, globalEncrypted] = await Promise.all([
    load('sessionEncrypted'),
    load('globalEncrypted'),
  ]);

  if (!sessionEncrypted || !globalEncrypted) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing required stored fields');
    throw new Error('[api/passcode] Missing required stored fields');
  }

  try {
    const [sessionJson, globalJson] = await Promise.all([
      aesDecrypt(sessionEncrypted, currentPasscodeHash),
      aesDecrypt(globalEncrypted, currentPasscodeHash),
    ]);

    return { sessionJson, globalJson };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Error decrypting session', err);
    throw err;
  }
}

export async function decryptSession(passcode: string) {
  const passcodeHash = await sha256(passcode);

  const [sessionEncrypted, globalEncrypted, sharedStateEncrypted] = await Promise.all([
    load('sessionEncrypted'),
    load('globalEncrypted'),
    load('sharedStateEncrypted'),
  ]);

  if (!sessionEncrypted || !globalEncrypted) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing required stored fields');
    throw new UnrecoverablePasscodeError('[api/passcode] Missing required stored fields');
  }

  try {
    // `sharedStateEncrypted` may be absent for sessions locked before it was persisted
    const [sessionJson, globalJson, sharedStateJson] = await Promise.all([
      aesDecrypt(sessionEncrypted, passcodeHash),
      aesDecrypt(globalEncrypted, passcodeHash),
      sharedStateEncrypted ? aesDecrypt(sharedStateEncrypted, passcodeHash) : undefined,
    ]);

    currentPasscodeHash = passcodeHash;

    return { sessionJson, globalJson, sharedStateJson };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Error decrypting session', err);
    throw err;
  }
}

export function forgetPasscode() {
  currentPasscodeHash = undefined;
}

export async function clearEncryptedSession(): Promise<void> {
  forgetPasscode();

  try {
    const adapter = getStorageAdapter();

    await removeChunkedItem(adapter, STORAGE_KEYS.passcodeSession);
    await removeChunkedItem(adapter, STORAGE_KEYS.passcodeGlobal);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[passcode] Failed to clear stored session', error);
  }

  await PASSCODE_IDB_STORE.clear();
}

function sha256(plaintext: string) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${plaintext}${SALT}`));
}

async function store(key: string, value: ArrayBuffer): Promise<void> {
  const asArray = Array.from(new Uint8Array(value));
  const serialized = JSON.stringify(asArray);

  try {
    const adapter = getStorageAdapter();
    const storageKey = key === 'sessionEncrypted'
      ? STORAGE_KEYS.passcodeSession
      : STORAGE_KEYS.passcodeGlobal;

    // Store in CloudStorage with chunking
    await setChunkedItem(adapter, storageKey, serialized);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[passcode] Failed to store in CloudStorage, falling back to IndexedDB', error);
  }

  // Also store in IndexedDB for backward compatibility
  return PASSCODE_IDB_STORE.set(key, asArray);
}

export async function hasEncryptedSession(): Promise<boolean> {
  try {
    const adapter = getStorageAdapter();
    const sessionData = await getChunkedItem(adapter, STORAGE_KEYS.passcodeSession);
    if (sessionData) return true;
  } catch (error) {
    // Ignore errors, fall back to IndexedDB check
  }

  // Fallback to IndexedDB
  const idbData = await PASSCODE_IDB_STORE.get('sessionEncrypted');
  return Boolean(idbData);
}

async function load(key: string): Promise<ArrayBuffer | undefined> {
  try {
    const adapter = getStorageAdapter();
    const storageKey = key === 'sessionEncrypted'
      ? STORAGE_KEYS.passcodeSession
      : STORAGE_KEYS.passcodeGlobal;

    // Try to load from CloudStorage
    const serialized = await getChunkedItem(adapter, storageKey);
    if (serialized) {
      const asArray = JSON.parse(serialized) as number[];
      return new Uint8Array(asArray).buffer;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[passcode] Failed to load from CloudStorage, falling back to IndexedDB', error);
  }

  // Fallback to IndexedDB
  const cached = await PASSCODE_IDB_STORE.get<number[]>(key);
  if (cached) {
    const asArrayBuffer = new Uint8Array(cached).buffer;
    return asArrayBuffer;
  }

  return undefined;
}

async function aesEncrypt(plaintext: string, pwHash: ArrayBuffer) {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const alg = { name: 'AES-GCM', iv };
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['encrypt']);
  const ptUint8 = new TextEncoder().encode(plaintext);
  const ctBuffer = await crypto.subtle.encrypt(alg, key, ptUint8);
  const ct = new Uint8Array(ctBuffer);
  const result = new Uint8Array(IV_LENGTH + ct.length);
  result.set(iv, 0);
  result.set(ct, IV_LENGTH);
  return result.buffer;
}

async function aesDecrypt(data: ArrayBuffer, pwHash: ArrayBuffer) {
  const dataArray = new Uint8Array(data);
  const iv = dataArray.slice(0, IV_LENGTH);
  const alg = { name: 'AES-GCM', iv };
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['decrypt']);
  const ct = dataArray.slice(IV_LENGTH);
  const plainBuffer = await crypto.subtle.decrypt(alg, key, ct);

  return new TextDecoder().decode(plainBuffer);
}
